import { useState, useEffect, useRef, useCallback } from "react";
import { ReactComponent as CocoSvg } from "./coco.svg";
import { track } from "./analytics";

/* ─── TTS ─────────────────────────────────────────────────── */
function useTTS() {
  const allVoices = useRef([]);
  const esRef     = useRef(null);
  const enRef     = useRef(null);
  const [list,    setList]    = useState([]);
  const [selName, setSelName] = useState("");

  const load = useCallback(() => {
    const vs = window.speechSynthesis ? window.speechSynthesis.getVoices() : [];
    allVoices.current = vs;
    const esVs = vs.filter(v => v.lang.toLowerCase().startsWith("es"));
    const enVs = vs.filter(v => v.lang.toLowerCase().startsWith("en"));
    const rank = v => {
      const n = v.name.toLowerCase();
      if (n.includes("neural") || n.includes("enhanced") || n.includes("wavenet") || n.includes("premium")) return 3;
      if (v.localService) return 2;
      return 1;
    };
    esVs.sort((a, b) => rank(b) - rank(a));
    enVs.sort((a, b) => rank(b) - rank(a));
    setList(esVs);
    if (esVs.length && !esRef.current) { esRef.current = esVs[0]; setSelName(esVs[0].name); }
    if (enVs.length && !enRef.current) enRef.current = enVs[0];
  }, []);

  useEffect(() => {
    load();
    if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = load;
    const t = setTimeout(load, 600);
    return () => clearTimeout(t);
  }, [load]);

  const say = useCallback((text, lang = "es", rate = 0.76, pitch = 1.14) => {
    if (!window.speechSynthesis || !text) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang   = lang === "es" ? "es-ES" : "en-US";
    u.rate   = rate;
    u.pitch  = pitch;
    u.volume = 1;
    const v  = lang === "es" ? esRef.current : enRef.current;
    if (v) u.voice = v;
    window.speechSynthesis.speak(u);
  }, []);

  const pick = useCallback((name) => {
    const v = allVoices.current.find(v => v.name === name);
    if (v) { esRef.current = v; setSelName(name); }
  }, []);

  return { say, list, selName, pick };
}

/* ─── SOUND FX ────────────────────────────────────────────── */
function useSnd() {
  const ctxRef = useRef(null);
  const ctx    = () => {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return ctxRef.current;
  };
  const tone = (freq, type, dur, vol = 0.13, delay = 0) => {
    try {
      const c = ctx(), o = c.createOscillator(), g = c.createGain();
      o.connect(g); g.connect(c.destination);
      o.type = type;
      o.frequency.setValueAtTime(freq, c.currentTime + delay);
      g.gain.setValueAtTime(0, c.currentTime + delay);
      g.gain.linearRampToValueAtTime(vol, c.currentTime + delay + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + delay + dur);
      o.start(c.currentTime + delay);
      o.stop(c.currentTime + delay + dur + 0.05);
    } catch (_) {}
  };
  return {
    tap:   () => tone(520, "sine", 0.09, 0.12),
    yay:   () => { [0, .13, .26, .4].forEach((d, i) => tone([523,659,784,1047][i], "sine", 0.22, 0.2, d)); },
    oops:  () => { tone(300, "sawtooth", 0.12, 0.1); tone(240, "sawtooth", 0.12, 0.1, .12); },
    magic: () => { [0,.08,.16,.24,.32].forEach((d, i) => tone([880,1047,1319,1047,1319][i], "sine", 0.16, 0.18, d)); },
    pop:   () => tone(660, "sine", 0.08, 0.12),
    stamp: () => { [523,659,784,1047,1319].forEach((n, i) => tone(n, "sine", 0.25, 0.2, i * 0.1)); },
  };
}

/* ─── DATA ────────────────────────────────────────────────── */
const WORLDS = [
  {
    id: "caribbean", name: "Caribbean Islands", flag: "🏝️",
    col: "#00ACC1", light: "#E0F7FA",
    bg: "linear-gradient(170deg,#0097A7,#26C6A6 60%,#FFF176)",
    episodes: [
      {
        id: "greet", title: "Say Hello!", icon: "👋",
        vocab: [
          { es: "hola",    en: "hello",     col: "#00BCD4", art: "greet"  },
          { es: "adios",   en: "bye bye",   col: "#FF6B9D", art: "wave"   },
          { es: "gracias", en: "thank you", col: "#FFB300", art: "thanks" },
          { es: "amigo",   en: "friend",    col: "#43A047", art: "friend" },
        ],
        song: { title: "Hola Cancion", lines: ["Hola hola hola", "Hola means hello", "Adios adios", "Bye bye means adios"] },
        activity: "Draw Mochachito on the beach saying Hola and write the word.",
        realWorld: "Say Hola to everyone you see today instead of hello!",
      },
      {
        id: "colors", title: "Rainbow Colors!", icon: "🌈",
        vocab: [
          { es: "rojo",     en: "red",    col: "#F44336", art: "red"    },
          { es: "azul",     en: "blue",   col: "#2196F3", art: "blue"   },
          { es: "verde",    en: "green",  col: "#4CAF50", art: "green"  },
          { es: "amarillo", en: "yellow", col: "#FFC107", art: "yellow" },
        ],
        song: { title: "Colors Song", lines: ["Rojo rojo red like the sun", "Azul azul blue like the sea", "Verde verde green like trees", "Amarillo yellow and free"] },
        activity: "Color the fish using each Spanish color!",
        realWorld: "Find something rojo, azul, and verde at home!",
      },
    ],
  },
  {
    id: "amazon", name: "Amazon Rainforest", flag: "🌳",
    col: "#388E3C", light: "#E8F5E9",
    bg: "linear-gradient(170deg,#2E7D32,#66BB6A 60%,#F9A825)",
    episodes: [
      {
        id: "animals", title: "Jungle Friends!", icon: "🐆",
        vocab: [
          { es: "jaguar", en: "jaguar", col: "#FF8F00", art: "jaguar" },
          { es: "mono",   en: "monkey", col: "#795548", art: "monkey" },
          { es: "loro",   en: "parrot", col: "#E91E63", art: "parrot" },
          { es: "rana",   en: "frog",   col: "#4CAF50", art: "frog"   },
        ],
        song: { title: "Jungle Friends", lines: ["Jaguar jaguar runs so fast", "Mono mono swings past", "Loro loro talks to me", "Rana rana hops with glee"] },
        activity: "Draw your favorite jungle animal and label it!",
        realWorld: "Make animal sounds! Roar like a jaguar!",
      },
    ],
  },
];

const ALL_WORDS = WORLDS.flatMap(w => w.episodes.flatMap(e => e.vocab));

function shuffle(a) { return [...a].sort(() => Math.random() - 0.5); }

/* ─── CSS ANIMATIONS ──────────────────────────────────────── */
const CSS = `
@keyframes fl       { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
@keyframes bop      { 0%,100%{transform:scale(1) rotate(0deg)} 35%{transform:scale(1.12) rotate(-5deg)} 65%{transform:scale(1.12) rotate(5deg)} }
@keyframes pop      { 0%{transform:scale(0.2);opacity:0} 70%{transform:scale(1.18)} 100%{transform:scale(1);opacity:1} }
@keyframes shake    { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-9px)} 75%{transform:translateX(9px)} }
@keyframes pulse    { 0%,100%{transform:scale(1)} 50%{transform:scale(1.07)} }
@keyframes cfal     { to{transform:translateY(110vh) rotate(400deg);opacity:0} }
@keyframes wink     { 0%,86%,100%{transform:scaleY(1)} 93%{transform:scaleY(0.07)} }
@keyframes earL     { 0%,100%{transform:rotate(0deg)} 50%{transform:rotate(-11deg)} }
@keyframes earR     { 0%,100%{transform:rotate(0deg)} 50%{transform:rotate(11deg)} }
@keyframes tailW    { 0%,100%{transform:rotate(-18deg)} 50%{transform:rotate(28deg)} }
@keyframes tailF    { 0%,100%{transform:rotate(-28deg)} 50%{transform:rotate(42deg)} }
@keyframes slideUp  { from{transform:translateY(50px);opacity:0} to{transform:translateY(0);opacity:1} }
@keyframes starPop  { 0%{transform:scale(0) rotate(-20deg);opacity:0} 60%{transform:scale(1.3) rotate(10deg);opacity:1} 100%{transform:scale(1) rotate(0);opacity:1} }
@keyframes twinkle  { 0%,100%{opacity:.4;transform:scale(.8)} 50%{opacity:1;transform:scale(1.2)} }
@keyframes heartbeat{ 0%,100%{transform:scale(1)} 14%{transform:scale(1.18)} 28%{transform:scale(1)} }
`;

/* ─── CONFETTI ────────────────────────────────────────────── */
function Confetti() {
  const items = Array.from({ length: 28 }, (_, i) => ({
    x: Math.random() * 100,
    c: ["#FF6B9D","#FFD700","#00BCD4","#7E57C2","#FF5252","#43A047","#FF9800"][i % 7],
    d: Math.random() * 1.5 + 0.5,
    del: Math.random() * 0.6,
    w: i % 2 ? 13 : 8,
    round: i % 3 === 0,
  }));
  return (
    <div style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:999 }}>
      {items.map((x, i) => (
        <div key={i} style={{
          position:"absolute", left: x.x + "%", top:"-20px",
          width: x.w, height: x.w,
          borderRadius: x.round ? "50%" : 3,
          background: x.c,
          animation: "cfal " + x.d + "s " + x.del + "s ease-in forwards",
        }}/>
      ))}
    </div>
  );
}

/* ─── COCO ────────────────────────────────────────────────── */
function Coco({ mood = "idle", size = 160, col = "#26C6A6", still = false }) {
  const cheering = mood === "cheer" || mood === "done" || mood === "excited";
  const sad      = mood === "wrong" || mood === "think";
  const curious  = mood === "curious";
  const singing  = mood === "sing";
  const dancing  = mood === "dance" || singing;

  const anim = still ? "none"
    : cheering ? "bop 0.55s ease-in-out infinite"
    : dancing            ? "bop 0.75s ease-in-out infinite"
    : curious            ? "pulse 1.6s ease-in-out infinite"
    : sad                ? "none"
    : "fl 2.6s ease-in-out infinite";

  const tilt = curious ? "rotate(-7deg)" : sad ? "rotate(5deg)" : "none";

  return (
    <div style={{ position:"relative", width:size, height:size, animation:anim }}>
      <CocoSvg width={size} height={size} aria-label="Mochachito the dog" role="img"
        preserveAspectRatio="xMidYMid slice"
        style={{
          display:"block",
          borderRadius:"50%", border:"4px solid " + col,
          boxShadow:"0 8px 18px rgba(0,0,0,0.18)",
          transform:tilt, transition:"transform 0.3s ease, filter 0.3s ease",
          filter: sad ? "saturate(0.7) brightness(0.95)" : "none",
          userSelect:"none",
        }}/>
      {cheering && (
        <>
          <span style={{ position:"absolute", top:"-6%", left:"-5%", fontSize:size*0.15, animation:"twinkle 0.9s ease-in-out infinite" }}>✨</span>
          <span style={{ position:"absolute", top:"-3%", right:"-6%", fontSize:size*0.13, animation:"twinkle 0.9s ease-in-out 0.25s infinite" }}>⭐</span>
        </>
      )}
      {singing && (
        <>
          <span style={{ position:"absolute", top:"8%", right:"-9%", fontSize:size*0.16, color:col, animation:"fl 1s ease-in-out infinite" }}>♪</span>
          <span style={{ position:"absolute", top:"32%", right:"-14%", fontSize:size*0.12, color:"#FFD700", animation:"fl 1.4s ease-in-out infinite" }}>♫</span>
        </>
      )}
      {sad && (
        <span style={{ position:"absolute", top:"2%", left:"-4%", fontSize:size*0.15 }}>💭</span>
      )}
    </div>
  );
}

/* ─── WORD ART ────────────────────────────────────────────── */
function WordArt({ art, color, size = 240 }) {
  const w = size, h = Math.round(size * 0.68);
  const arts = {
    greet: (
      <svg width={w} height={h} viewBox="0 0 300 204">
        <defs>
          <linearGradient id="wa_greet_sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#87CEEB"/><stop offset="100%" stopColor="#E0F7FA"/>
          </linearGradient>
        </defs>
        <rect width="300" height="204" fill="url(#wa_greet_sky)"/>
        <circle cx="234" cy="34" r="26" fill="#FFF9C4"/>
        <circle cx="234" cy="34" r="20" fill="#FFD700"/>
        {[0,45,90,135,180,225,270,315].map((a, i) => {
          const r = a * Math.PI / 180;
          return <line key={i} x1={234+28*Math.cos(r)} y1={34+28*Math.sin(r)} x2={234+42*Math.cos(r)} y2={34+42*Math.sin(r)} stroke="#FFD700" strokeWidth="2.5" strokeLinecap="round"/>;
        })}
        <ellipse cx="60" cy="46" rx="34" ry="16" fill="white" opacity="0.9"/>
        <ellipse cx="42" cy="52" rx="20" ry="13" fill="white" opacity="0.9"/>
        <ellipse cx="80" cy="52" rx="22" ry="14" fill="white" opacity="0.9"/>
        <path d="M0 126 Q38 114 75 124 Q112 134 150 120 Q188 106 225 122 Q262 138 300 118 L300 204 L0 204 Z" fill="#0097A7"/>
        <path d="M0 158 Q75 146 150 154 Q225 162 300 148 L300 204 L0 204 Z" fill="#FFE082"/>
        <path d="M30 204 Q26 178 28 156 Q30 136 32 116" stroke="#5D4037" strokeWidth="8" fill="none" strokeLinecap="round"/>
        <path d="M32 118 Q6 96 -10 80"  stroke="#388E3C" strokeWidth="8" fill="none" strokeLinecap="round"/>
        <path d="M32 118 Q58 94 74 78"  stroke="#43A047" strokeWidth="8" fill="none" strokeLinecap="round"/>
        <ellipse cx="90"  cy="174" rx="8" ry="5" fill="#FF8A65" opacity="0.9"/>
        <ellipse cx="210" cy="176" rx="7" ry="4" fill="#FFB3C6" opacity="0.9"/>
        <text x="148" y="112" fontSize="44" textAnchor="middle">👋</text>
        <text x="196" y="118" fontSize="32" textAnchor="middle">👋</text>
      </svg>
    ),
    wave: (
      <svg width={w} height={h} viewBox="0 0 300 204">
        <defs>
          <linearGradient id="wa_wave_bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FF8A65"/><stop offset="50%" stopColor="#FF6B9D"/><stop offset="100%" stopColor="#7E57C2"/>
          </linearGradient>
        </defs>
        <rect width="300" height="204" fill="url(#wa_wave_bg)"/>
        <circle cx="150" cy="80" r="50" fill="#FFD700" opacity="0.9"/>
        <circle cx="150" cy="80" r="38" fill="#FF8F00"/>
        <path d="M0 155 Q75 140 150 150 Q225 160 300 142 L300 204 L0 204 Z" fill="#1565C0" opacity="0.7"/>
        <path d="M0 172 Q75 162 150 168 Q225 175 300 162 L300 204 L0 204 Z" fill="#FFE082"/>
        <path d="M112 162 Q150 148 188 162 L183 175 L117 175 Z" fill="#4E342E"/>
        <path d="M150 148 L150 120 L178 136 Z" fill="#FF6B9D" opacity="0.85"/>
        <text x="150" y="118" fontSize="42" textAnchor="middle">👋</text>
      </svg>
    ),
    thanks: (
      <svg width={w} height={h} viewBox="0 0 300 204">
        <defs>
          <linearGradient id="wa_thanks_bg" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#FFF8E1"/><stop offset="100%" stopColor="#FFE0B2"/>
          </linearGradient>
        </defs>
        <rect width="300" height="204" fill="url(#wa_thanks_bg)"/>
        {[[50,38,"#FF6B9D"],[250,38,"#FF9800"],[30,148,"#43A047"],[268,158,"#9C27B0"],[150,18,"#2196F3"]].map(([x,y,c], i) => (
          <g key={i}>
            {[0,72,144,216,288].map((a, j) => {
              const r = a * Math.PI / 180;
              return <ellipse key={j} cx={x+Math.cos(r)*12} cy={y+Math.sin(r)*12} rx="7" ry="12" fill={c} opacity="0.8"
                transform={"rotate(" + a + "," + (x+Math.cos(r)*12) + "," + (y+Math.sin(r)*12) + ")"}/>;
            })}
            <circle cx={x} cy={y} r="7" fill="#FFD700"/>
          </g>
        ))}
        <path d="M150 160 C150 160 80 120 80 78 C80 55 100 44 120 52 C134 58 150 74 150 74 C150 74 166 58 180 52 C200 44 220 55 220 78 C220 120 150 160 150 160 Z" fill="#FF4081" opacity="0.9"/>
        <text x="150" y="186" fontSize="32" textAnchor="middle">🙏</text>
      </svg>
    ),
    friend: (
      <svg width={w} height={h} viewBox="0 0 300 204">
        <defs>
          <linearGradient id="wa_friend_bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E1F5FE"/><stop offset="100%" stopColor="#B2EBF2"/>
          </linearGradient>
        </defs>
        <rect width="300" height="204" fill="url(#wa_friend_bg)"/>
        <path d="M0 175 Q75 162 150 170 Q225 178 300 163 L300 204 L0 204 Z" fill="#66BB6A"/>
        {[[40,170,"#FF6B9D"],[90,173,"#FFD700"],[150,168,"#FF9800"],[210,171,"#9C27B0"],[260,169,"#FF4081"]].map(([x,y,c], i) => (
          <g key={i}>
            <line x1={x} y1={y} x2={x} y2={y+12} stroke="#43A047" strokeWidth="2"/>
            {[0,72,144,216,288].map((a, j) => {
              const r = a * Math.PI / 180;
              return <ellipse key={j} cx={x+Math.cos(r)*5} cy={y+Math.sin(r)*5} rx="3.5" ry="6" fill={c} opacity="0.85"
                transform={"rotate(" + a + "," + (x+Math.cos(r)*5) + "," + (y+Math.sin(r)*5) + ")"}/>;
            })}
            <circle cx={x} cy={y} r="3.5" fill="#FFD700"/>
          </g>
        ))}
        <circle cx="100" cy="120" r="22" fill="#FFD54F"/>
        <circle cx="200" cy="120" r="22" fill="#FF8A65"/>
        <rect x="86"  y="142" width="28" height="32" rx="10" fill="#42A5F5"/>
        <rect x="186" y="142" width="28" height="32" rx="10" fill="#EF5350"/>
        <path d="M114 152 Q150 145 186 152" stroke="#FFA726" strokeWidth="6" fill="none" strokeLinecap="round"/>
        <circle cx="150" cy="148" r="8" fill="#FFD700"/>
        <circle cx="94"  cy="118" r="4" fill="#2C1600"/>
        <circle cx="106" cy="118" r="4" fill="#2C1600"/>
        <path d="M92 127 Q100 133 108 127" stroke="#2C1600" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <circle cx="194" cy="118" r="4" fill="#2C1600"/>
        <circle cx="206" cy="118" r="4" fill="#2C1600"/>
        <path d="M192 127 Q200 133 208 127" stroke="#2C1600" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <text x="150" y="80" fontSize="28" textAnchor="middle">❤️</text>
      </svg>
    ),
    red: (
      <svg width={w} height={h} viewBox="0 0 300 204">
        <defs>
          <radialGradient id="wa_red_bg" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#FFEBEE"/><stop offset="100%" stopColor="#FFCDD2"/>
          </radialGradient>
        </defs>
        <rect width="300" height="204" fill="url(#wa_red_bg)"/>
        <path d="M150 178 C100 178 60 146 60 106 C60 66 90 42 120 42 C132 42 142 48 150 56 C158 48 168 42 180 42 C210 42 240 66 240 106 C240 146 200 178 150 178 Z" fill="#F44336"/>
        <path d="M116 62 C108 42 116 26 130 22" stroke="#2E7D32" strokeWidth="5" fill="none" strokeLinecap="round"/>
        <ellipse cx="136" cy="18" rx="16" ry="10" fill="#43A047" transform="rotate(-20,136,18)"/>
        <ellipse cx="112" cy="88" rx="18" ry="28" fill="rgba(255,255,255,0.22)" transform="rotate(-20,112,88)"/>
        <path d="M30 148 C20 138 22 122 34 120 C40 120 44 124 46 128 C48 124 52 120 58 120 C70 122 72 138 62 148 L46 162 Z" fill="#F44336" opacity="0.75"/>
      </svg>
    ),
    blue: (
      <svg width={w} height={h} viewBox="0 0 300 204">
        <defs>
          <linearGradient id="wa_blue_bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E3F2FD"/><stop offset="100%" stopColor="#BBDEFB"/>
          </linearGradient>
        </defs>
        <rect width="300" height="204" fill="url(#wa_blue_bg)"/>
        <path d="M0 116 Q38 101 75 114 Q112 127 150 111 Q188 95 225 111 Q262 127 300 107 L300 204 L0 204 Z" fill="#1565C0" opacity="0.8"/>
        <path d="M0 162 Q75 152 150 158 Q225 165 300 154 L300 204 L0 204 Z" fill="#1976D2"/>
        {[[50,28],[80,53],[130,18],[170,43],[220,28],[260,56]].map(([x,y], i) => (
          <ellipse key={i} cx={x} cy={y} rx="3" ry="8" fill="#2196F3" opacity="0.6" transform={"rotate(10," + x + "," + y + ")"}/>
        ))}
        <path d="M150 138 L150 58" stroke="#0D47A1" strokeWidth="4" strokeLinecap="round"/>
        <path d="M80 98 Q150 48 220 98 Z" fill="#1976D2"/>
        <path d="M80 98 Q150 48 220 98" stroke="#0D47A1" strokeWidth="2.5" fill="none"/>
        <ellipse cx="54" cy="68" rx="16" ry="10" fill="#1E88E5" opacity="0.8" transform="rotate(-20,54,68)"/>
        <ellipse cx="70" cy="73" rx="13" ry="8"  fill="#42A5F5" opacity="0.8" transform="rotate(20,70,73)"/>
      </svg>
    ),
    green: (
      <svg width={w} height={h} viewBox="0 0 300 204">
        <defs>
          <linearGradient id="wa_green_bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E8F5E9"/><stop offset="100%" stopColor="#C8E6C9"/>
          </linearGradient>
        </defs>
        <rect width="300" height="204" fill="url(#wa_green_bg)"/>
        <path d="M0 155 Q75 115 150 135 Q225 155 300 125 L300 204 L0 204 Z" fill="#388E3C"/>
        <path d="M0 172 Q75 146 150 158 Q225 170 300 151 L300 204 L0 204 Z" fill="#43A047"/>
        {[[50,148,"#2E7D32"],[200,142,"#2E7D32"],[260,146,"#43A047"]].map(([x,y,c], i) => (
          <g key={i}>
            <rect x={x-4} y={y} width="8" height={204-y} fill="#5D4037"/>
            <ellipse cx={x} cy={y-8}  rx="20" ry="26" fill={c}/>
            <ellipse cx={x} cy={y-20} rx="14" ry="20" fill="#43A047"/>
          </g>
        ))}
        <ellipse cx="148" cy="143" rx="20" ry="14" fill="#4CAF50"/>
        <ellipse cx="148" cy="138" rx="16" ry="10" fill="#66BB6A"/>
        <circle cx="139" cy="130" r="3.5" fill="#1B5E20"/>
        <circle cx="155" cy="130" r="3.5" fill="#1B5E20"/>
        <path d="M142 142 Q148 146 154 142" stroke="#2E7D32" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
        {[0,1,2,3,4].map(i => (
          <circle key={i} cx={60+i*22} cy={172} r="10" fill={["#8BC34A","#66BB6A","#4CAF50","#43A047","#388E3C"][i]}/>
        ))}
      </svg>
    ),
    yellow: (
      <svg width={w} height={h} viewBox="0 0 300 204">
        <defs>
          <linearGradient id="wa_yellow_bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFFDE7"/><stop offset="100%" stopColor="#FFF9C4"/>
          </linearGradient>
        </defs>
        <rect width="300" height="204" fill="url(#wa_yellow_bg)"/>
        <circle cx="150" cy="86" r="65" fill="#FFF176" opacity="0.8"/>
        <circle cx="150" cy="86" r="52" fill="#FFD700"/>
        <circle cx="150" cy="86" r="40" fill="#FFC107"/>
        <circle cx="132" cy="74" r="12" fill="#FFE57F" opacity="0.5"/>
        {[0,30,60,90,120,150,180,210,240,270,300,330].map((a, i) => {
          const r = a * Math.PI / 180;
          return <line key={i} x1={150+58*Math.cos(r)} y1={86+58*Math.sin(r)} x2={150+75*Math.cos(r)} y2={86+75*Math.sin(r)} stroke="#FFB300" strokeWidth="4" strokeLinecap="round"/>;
        })}
        {[[50,172,"#4CAF50"],[150,180,"#2E7D32"],[250,174,"#388E3C"]].map(([x,y,sc], i) => (
          <g key={i}>
            <path d={"M" + x + " " + y + " Q" + (x-5) + " " + (y-18) + " " + x + " " + (y-36)} stroke={sc} strokeWidth="5" fill="none" strokeLinecap="round"/>
            {[0,45,90,135,180,225,270,315].map((a, j) => {
              const rr = a * Math.PI / 180;
              return <ellipse key={j} cx={x+Math.cos(rr)*11} cy={y-36+Math.sin(rr)*11} rx="5" ry="10" fill="#FFD700"
                transform={"rotate(" + a + "," + (x+Math.cos(rr)*11) + "," + (y-36+Math.sin(rr)*11) + ")"}/>;
            })}
            <circle cx={x} cy={y-36} r="9" fill="#5D4037"/>
          </g>
        ))}
      </svg>
    ),
    jaguar: (
      <svg width={w} height={h} viewBox="0 0 300 204">
        <defs>
          <linearGradient id="wa_jag_bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFF8E1"/><stop offset="100%" stopColor="#FFE0B2"/>
          </linearGradient>
        </defs>
        <rect width="300" height="204" fill="url(#wa_jag_bg)"/>
        {[[0,38,"#2E7D32"],[260,28,"#388E3C"],[0,148,"#43A047"],[280,158,"#2E7D32"]].map(([x,y,c], i) => (
          <ellipse key={i} cx={x} cy={y} rx="36" ry="22" fill={c} opacity="0.6" transform={"rotate(" + (-20+i*10) + "," + x + "," + y + ")"}/>
        ))}
        <ellipse cx="155" cy="148" rx="72" ry="42" fill="#FF8F00"/>
        <ellipse cx="155" cy="145" rx="65" ry="36" fill="#FFA726"/>
        {[[130,133],[170,128],[115,153],[188,150],[148,160]].map(([x,y], i) => (
          <g key={i}>
            <ellipse cx={x} cy={y} rx="10" ry="8" fill="#5D4037" opacity="0.6"/>
            <ellipse cx={x} cy={y} rx="6"  ry="4.5" fill="#FF8F00" opacity="0.5"/>
          </g>
        ))}
        <path d="M220 148 Q248 134 254 116 Q258 102 248 98" stroke="#FF8F00" strokeWidth="12" fill="none" strokeLinecap="round"/>
        <circle cx="100" cy="130" r="44" fill="#FF8F00"/>
        <circle cx="100" cy="128" r="40" fill="#FFA726"/>
        <ellipse cx="100" cy="142" rx="24" ry="16" fill="#FFF8E1"/>
        <ellipse cx="88"  cy="120" rx="10" ry="9" fill="#1a0800"/>
        <ellipse cx="88"  cy="120" rx="6"  ry="7" fill="#4CAF50"/>
        <circle  cx="91"  cy="117" r="3.5" fill="white"/>
        <ellipse cx="112" cy="120" rx="10" ry="9" fill="#1a0800"/>
        <ellipse cx="112" cy="120" rx="6"  ry="7" fill="#4CAF50"/>
        <circle  cx="115" cy="117" r="3.5" fill="white"/>
        <ellipse cx="100" cy="134" rx="7" ry="5.5" fill="#2C1600"/>
        <path d="M88 146 Q100 152 112 146" stroke="#5D4037" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
        <path d="M74 90 Q60 54 72 18 Q77 4 87 2 Q97 0 100 20 Q102 42 96 88 Z" fill="#D4700A" transform="rotate(-20,74,90)"/>
        <path d="M126 88 Q140 52 128 16 Q123 2 113 0 Q103 -2 100 18 Q98 40 104 86 Z" fill="#D4700A" transform="rotate(20,126,88)"/>
      </svg>
    ),
    monkey: (
      <svg width={w} height={h} viewBox="0 0 300 204">
        <defs>
          <linearGradient id="wa_monkey_bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E8F5E9"/><stop offset="100%" stopColor="#DCEDC8"/>
          </linearGradient>
        </defs>
        <rect width="300" height="204" fill="url(#wa_monkey_bg)"/>
        <path d="M-10 76 Q80 61 160 74 Q220 84 310 66" stroke="#5D4037" strokeWidth="18" fill="none" strokeLinecap="round"/>
        <path d="M-10 76 Q80 61 160 74 Q220 84 310 66" stroke="#795548" strokeWidth="12" fill="none" strokeLinecap="round"/>
        {[[40,56,"#43A047"],[90,51,"#388E3C"],[140,61,"#4CAF50"],[200,64,"#2E7D32"]].map(([x,y,c], i) => (
          <ellipse key={i} cx={x} cy={y} rx="18" ry="10" fill={c} opacity="0.85" transform={"rotate(" + (-20+i*10) + "," + x + "," + y + ")"}/>
        ))}
        <ellipse cx="148" cy="156" rx="30" ry="22" fill="#795548"/>
        <ellipse cx="148" cy="154" rx="25" ry="18" fill="#8D6E63"/>
        <path d="M136 136 Q128 96 148 76" stroke="#6D4C41" strokeWidth="8" fill="none" strokeLinecap="round"/>
        <path d="M160 138 Q168 96 155 76" stroke="#6D4C41" strokeWidth="8" fill="none" strokeLinecap="round"/>
        <circle cx="148" cy="76" r="9" fill="#795548"/>
        <circle cx="155" cy="76" r="9" fill="#795548"/>
        <circle cx="148" cy="124" r="30" fill="#795548"/>
        <circle cx="148" cy="122" r="26" fill="#8D6E63"/>
        <ellipse cx="148" cy="130" rx="18" ry="14" fill="#BCAAA4"/>
        <circle cx="140" cy="116" r="7" fill="#1a0800"/>
        <circle cx="142" cy="114" r="2.5" fill="white"/>
        <circle cx="156" cy="116" r="7" fill="#1a0800"/>
        <circle cx="158" cy="114" r="2.5" fill="white"/>
        <circle cx="148" cy="124" r="4" fill="#5D4037"/>
        <path d="M140 132 Q148 138 156 132" stroke="#5D4037" strokeWidth="2.2" fill="none" strokeLinecap="round"/>
        <circle cx="120" cy="122" r="12" fill="#795548"/>
        <circle cx="120" cy="122" r="7"  fill="#BCAAA4"/>
        <circle cx="176" cy="122" r="12" fill="#795548"/>
        <circle cx="176" cy="122" r="7"  fill="#BCAAA4"/>
        <path d="M200 146 Q218 132 230 144 Q224 160 210 164 Q196 162 200 146 Z" fill="#FFD700"/>
      </svg>
    ),
    parrot: (
      <svg width={w} height={h} viewBox="0 0 300 204">
        <defs>
          <linearGradient id="wa_parrot_bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E8EAF6"/><stop offset="100%" stopColor="#C5CAE9"/>
          </linearGradient>
        </defs>
        <rect width="300" height="204" fill="url(#wa_parrot_bg)"/>
        <path d="M60 156 Q150 144 240 154" stroke="#5D4037" strokeWidth="12" fill="none" strokeLinecap="round"/>
        <path d="M60 156 Q150 144 240 154" stroke="#795548" strokeWidth="8"  fill="none" strokeLinecap="round"/>
        <ellipse cx="150" cy="124" rx="36" ry="46" fill="#E91E63"/>
        <ellipse cx="150" cy="118" rx="30" ry="40" fill="#F06292"/>
        <path d="M118 116 Q90 106 76 126 Q70 146 88 154 Q104 160 118 144 Z" fill="#AB47BC"/>
        <path d="M182 116 Q210 106 224 126 Q230 146 212 154 Q196 160 182 144 Z" fill="#1565C0"/>
        <path d="M135 166 Q128 188 120 200" stroke="#FFD700" strokeWidth="8" fill="none" strokeLinecap="round"/>
        <path d="M150 168 L150 204"         stroke="#4CAF50" strokeWidth="8" fill="none" strokeLinecap="round"/>
        <path d="M165 166 Q172 188 180 200" stroke="#FF5722" strokeWidth="8" fill="none" strokeLinecap="round"/>
        <circle cx="150" cy="78" r="34" fill="#E91E63"/>
        <circle cx="150" cy="76" r="30" fill="#F48FB1"/>
        {[-16,-8,0,8,16].map((dx, i) => (
          <ellipse key={i} cx={150+dx} cy={44+Math.abs(dx)*0.3} rx="5" ry="12"
            fill={["#FFD700","#4CAF50","#E91E63","#2196F3","#FF9800"][i]}
            transform={"rotate(" + (dx*0.5) + "," + (150+dx) + ",44)"}/>
        ))}
        <circle cx="138" cy="74" r="9" fill="white"/>
        <circle cx="162" cy="74" r="9" fill="white"/>
        <circle cx="139" cy="75" r="5.5" fill="#1a0800"/>
        <circle cx="141" cy="73" r="2.5" fill="white"/>
        <circle cx="163" cy="75" r="5.5" fill="#1a0800"/>
        <circle cx="165" cy="73" r="2.5" fill="white"/>
        <path d="M142 88 Q150 100 158 88 Q154 82 150 80 Q146 82 142 88 Z" fill="#F57F17"/>
      </svg>
    ),
    frog: (
      <svg width={w} height={h} viewBox="0 0 300 204">
        <defs>
          <linearGradient id="wa_frog_bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E8F5E9"/><stop offset="100%" stopColor="#A5D6A7"/>
          </linearGradient>
        </defs>
        <rect width="300" height="204" fill="url(#wa_frog_bg)"/>
        <ellipse cx="150" cy="182" rx="130" ry="28" fill="#1976D2" opacity="0.6"/>
        <ellipse cx="150" cy="176" rx="50"  ry="18" fill="#43A047"/>
        {[0,72,144,216,288].map((a, i) => {
          const r = a * Math.PI / 180;
          return <ellipse key={i} cx={150+Math.cos(r)*14} cy={162+Math.sin(r)*14} rx="7" ry="12" fill="#FF80AB" opacity="0.9"
            transform={"rotate(" + a + "," + (150+Math.cos(r)*14) + "," + (162+Math.sin(r)*14) + ")"}/>;
        })}
        <circle cx="150" cy="162" r="7" fill="#FFD700"/>
        <ellipse cx="150" cy="148" rx="44" ry="32" fill="#4CAF50"/>
        <ellipse cx="150" cy="144" rx="38" ry="26" fill="#66BB6A"/>
        <ellipse cx="150" cy="152" rx="26" ry="20" fill="#C8E6C9"/>
        <path d="M108 162 Q88 174 78 166 Q68 156 76 148" stroke="#43A047" strokeWidth="12" fill="none" strokeLinecap="round"/>
        <path d="M192 162 Q212 174 222 166 Q232 156 224 148" stroke="#43A047" strokeWidth="12" fill="none" strokeLinecap="round"/>
        <ellipse cx="150" cy="112" rx="40" ry="32" fill="#4CAF50"/>
        <ellipse cx="150" cy="110" rx="36" ry="28" fill="#66BB6A"/>
        <circle cx="128" cy="90" r="14" fill="#4CAF50"/>
        <circle cx="172" cy="90" r="14" fill="#4CAF50"/>
        <circle cx="128" cy="88" r="10" fill="#FFF9C4"/>
        <circle cx="172" cy="88" r="10" fill="#FFF9C4"/>
        <circle cx="129" cy="89" r="5.5" fill="#1a0800"/>
        <circle cx="131" cy="87" r="2.5" fill="white"/>
        <circle cx="173" cy="89" r="5.5" fill="#1a0800"/>
        <circle cx="175" cy="87" r="2.5" fill="white"/>
        <path d="M130 122 Q150 134 170 122" stroke="#2E7D32" strokeWidth="3" fill="none" strokeLinecap="round"/>
        {[[40,28],[70,13],[220,18],[260,38]].map(([x,y], i) => (
          <ellipse key={i} cx={x} cy={y} rx="2.5" ry="6" fill="#42A5F5" opacity="0.5" transform={"rotate(8," + x + "," + y + ")"}/>
        ))}
      </svg>
    ),
  };
  return arts[art] || (
    <svg width={w} height={h} viewBox="0 0 300 204">
      <rect width="300" height="204" rx="20" fill={color + "22"}/>
      <text x="150" y="120" textAnchor="middle" fontSize="80">?</text>
    </svg>
  );
}

/* ─── SCENE BACKGROUNDS ───────────────────────────────────── */
function Scene({ worldId }) {
  if (worldId === "caribbean") return (
    <svg viewBox="0 0 400 150" style={{ width:"100%", display:"block" }} preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="sc_csky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#29B6F6"/><stop offset="100%" stopColor="#B2EBF2"/>
        </linearGradient>
        <linearGradient id="sc_csea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0097A7"/><stop offset="100%" stopColor="#006064"/>
        </linearGradient>
        <radialGradient id="sc_csun" cx="80%" cy="18%">
          <stop offset="0%" stopColor="#FFF9C4"/>
          <stop offset="50%" stopColor="#FFD700"/>
          <stop offset="100%" stopColor="#FF9800" stopOpacity="0"/>
        </radialGradient>
      </defs>
      <rect width="400" height="150" fill="url(#sc_csky)"/>
      <circle cx="332" cy="28" r="24" fill="#FFF9C4"/>
      <circle cx="332" cy="28" r="18" fill="#FFD700"/>
      <circle cx="332" cy="28" r="52" fill="url(#sc_csun)"/>
      {[0,45,90,135,180,225,270,315].map((a, i) => {
        const r = a * Math.PI / 180;
        return <line key={i} x1={332+26*Math.cos(r)} y1={28+26*Math.sin(r)} x2={332+38*Math.cos(r)} y2={28+38*Math.sin(r)} stroke="#FFD700" strokeWidth="2.5" strokeLinecap="round"/>;
      })}
      <ellipse cx="60" cy="26" rx="32" ry="15" fill="white" opacity="0.85"/>
      <ellipse cx="42" cy="31" rx="19" ry="12" fill="white" opacity="0.85"/>
      <ellipse cx="80" cy="31" rx="21" ry="13" fill="white" opacity="0.85"/>
      <path d="M0 92 Q50 80 100 90 Q150 100 200 86 Q250 72 300 88 Q350 104 400 88 L400 150 L0 150 Z" fill="url(#sc_csea)"/>
      <path d="M0 122 Q100 110 200 118 Q300 126 400 112 L400 150 L0 150 Z" fill="#FFE082"/>
      <path d="M30 150 Q26 132 28 114 Q30 98 32 82" stroke="#5D4037" strokeWidth="8" fill="none" strokeLinecap="round"/>
      <path d="M32 84 Q6 62 -10 46"  stroke="#388E3C" strokeWidth="8" fill="none" strokeLinecap="round"/>
      <path d="M32 84 Q58 60 74 44"  stroke="#43A047" strokeWidth="8" fill="none" strokeLinecap="round"/>
      <circle cx="28" cy="94" r="6" fill="#5D4037"/>
      <path d="M370 150 Q374 134 372 116 Q370 100 368 84" stroke="#5D4037" strokeWidth="7" fill="none" strokeLinecap="round"/>
      <path d="M368 86 Q344 64 330 49" stroke="#43A047" strokeWidth="7" fill="none" strokeLinecap="round"/>
      <path d="M368 86 Q392 62 406 47" stroke="#388E3C" strokeWidth="7" fill="none" strokeLinecap="round"/>
      <ellipse cx="90"  cy="132" rx="8" ry="5" fill="#FF8A65" opacity="0.85"/>
      <ellipse cx="200" cy="134" rx="7" ry="4" fill="#FFB3C6" opacity="0.85"/>
      {[0,72,144,216,288].map((a, i) => {
        const r = a * Math.PI / 180;
        return <ellipse key={i} cx={128+Math.cos(r)*9} cy={128+Math.sin(r)*9} rx="5" ry="9" fill="#FF6B9D" opacity="0.85"
          transform={"rotate(" + a + "," + (128+Math.cos(r)*9) + "," + (128+Math.sin(r)*9) + ")"}/>;
      })}
      <circle cx="128" cy="128" r="5.5" fill="#FFD700"/>
    </svg>
  );
  return (
    <svg viewBox="0 0 400 150" style={{ width:"100%", display:"block" }} preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="sc_asky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1B5E20"/><stop offset="100%" stopColor="#2E7D32"/>
        </linearGradient>
        <radialGradient id="sc_abeam" cx="50%" cy="0%">
          <stop offset="0%" stopColor="#F9A825" stopOpacity="0.45"/>
          <stop offset="100%" stopColor="#F9A825" stopOpacity="0"/>
        </radialGradient>
      </defs>
      <rect width="400" height="150" fill="url(#sc_asky)"/>
      <ellipse cx="200" cy="0" rx="120" ry="180" fill="url(#sc_abeam)"/>
      {[0,60,120,180,240,300,360].map((x, i) => (
        <g key={i}>
          <rect x={x-5} y={52} width="10" height="98" fill="#3E2723" opacity="0.7"/>
          <ellipse cx={x} cy={50} rx="22" ry="34" fill="#1B5E20"/>
          <ellipse cx={x} cy={36} rx="16" ry="26" fill="#2E7D32"/>
          <ellipse cx={x} cy={24} rx="10" ry="18" fill="#388E3C"/>
        </g>
      ))}
      <path d="M0 112 Q50 100 100 110 Q150 120 200 104 Q250 88 300 104 Q350 120 400 100 L400 150 L0 150 Z" fill="#1565C0" opacity="0.55"/>
      <path d="M0 130 Q100 118 200 126 Q300 134 400 120 L400 150 L0 150 Z" fill="#33691E"/>
      {[[30,140],[100,144],[180,138],[260,142],[340,140]].map(([x,y], i) => (
        <g key={i}>
          {[-24,-14,-6,0,6,14,24].map((dx, j) => (
            <path key={j} d={"M" + x + " " + y + " Q" + (x+dx*0.9) + " " + (y-14) + " " + (x+dx*1.6) + " " + (y-26)}
              stroke="#66BB6A" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          ))}
        </g>
      ))}
    </svg>
  );
}

/* ─── VOICE PICKER ────────────────────────────────────────── */
function VoicePicker({ voices, selected, onPick, onClose }) {
  const [preview, setPreview] = useState(null);
  const test = (v) => {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance("Hola soy Mochachito vamos a aprender espanol");
    u.voice = v; u.lang = "es-ES"; u.rate = 0.78; u.pitch = 1.12;
    window.speechSynthesis.speak(u);
    setPreview(v.name);
  };
  const quality = (v) => {
    const n = v.name.toLowerCase();
    if (n.includes("neural") || n.includes("enhanced") || n.includes("wavenet")) return "Neural";
    if (v.localService) return "Local";
    return "Online";
  };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }} onClick={onClose}>
      <div style={{ background:"#fff", borderRadius:28, padding:"1.5rem", maxWidth:400, width:"100%", maxHeight:"82vh", overflowY:"auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem" }}>
          <div style={{ fontSize:17, fontWeight:800, color:"#333" }}>Choose Mochachito's Voice</div>
          <button onClick={onClose} style={{ background:"#f0f0f0", border:"none", borderRadius:99, padding:"7px 16px", cursor:"pointer", fontSize:14, fontWeight:700 }}>Done</button>
        </div>
        {voices.length === 0 && (
          <div style={{ background:"#FFF3E0", borderRadius:14, padding:"1rem", marginBottom:"1rem", fontSize:13, color:"#E65100" }}>
            No Spanish voices found. Try Chrome or Safari, or download Spanish voices in your system settings.
          </div>
        )}
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {voices.map(v => {
            const isSel = v.name === selected;
            return (
              <div key={v.name} style={{ border:"2.5px solid " + (isSel ? "#00BCD4" : "#eee"), borderRadius:16, padding:"10px 14px", background: isSel ? "#E0F7FA" : "#fafafa", display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"#333" }}>{v.name}</div>
                  <div style={{ fontSize:11, color:"#999", marginTop:2 }}>{v.lang} · {quality(v)}</div>
                </div>
                <button onClick={() => test(v)} style={{ background:"#f0f0f0", border:"none", borderRadius:99, padding:"6px 12px", cursor:"pointer", fontSize:12, fontWeight:700, color:"#555" }}>
                  {preview === v.name ? "Playing" : "Test"}
                </button>
                <button onClick={() => { onPick(v.name); setPreview(null); }} style={{ background: isSel ? "#00BCD4" : "#f0f0f0", border:"none", borderRadius:99, padding:"6px 12px", cursor:"pointer", fontSize:12, fontWeight:700, color: isSel ? "#fff" : "#555" }}>
                  {isSel ? "Active" : "Use"}
                </button>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop:"1rem", background:"#E8F5E9", borderRadius:14, padding:"0.75rem", fontSize:12, color:"#2E7D32" }}>
          <div style={{ fontWeight:800, marginBottom:4 }}>Get better voices:</div>
          <div>iPhone/Mac: Settings > Accessibility > Spoken Content > Voices > Spanish > Enhanced</div>
          <div style={{ marginTop:4 }}>Android: Settings > Accessibility > Text-to-Speech > Google TTS</div>
        </div>
      </div>
    </div>
  );
}

/* ─── PARENT DASHBOARD ────────────────────────────────────── */
function ParentDash({ name, doneEps, wordBank, onSaidIt }) {
  const [view, setView]       = useState("home");
  const [picked, setPicked]   = useState(null);
  const [done, setDone]       = useState(false);

  const exposed  = wordBank.filter(w => w.count > 0);
  const heardLot = wordBank.filter(w => w.count >= 4);
  const realLife = wordBank.filter(w => w.saidIt);

  const confLabel = (w) => {
    if (w.saidIt)    return "Said it out loud!";
    if (w.count >= 4) return "Heard a lot";
    if (w.count >= 2) return "Heard a few times";
    return "Just met";
  };

  if (view === "saidit") return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"1.5rem", textAlign:"center" }}>
      {!done ? (
        <>
          <div style={{ fontSize:48, marginBottom:8, animation:"heartbeat 1.5s ease-in-out infinite" }}>🌟</div>
          <div style={{ fontSize:20, fontWeight:800, color:"#333", marginBottom:4 }}>What did {name || "your child"} say?</div>
          <div style={{ fontSize:14, color:"#888", marginBottom:"1.5rem" }}>Tap the word they used</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:10, justifyContent:"center", maxWidth:360, marginBottom:"1.5rem" }}>
            {exposed.map(w => (
              <button key={w.es} onClick={() => setPicked(w.es)}
                style={{ background: picked === w.es ? "#00BCD4" : "#f5f5f5", border:"2.5px solid " + (picked === w.es ? "#00BCD4" : "#ddd"), borderRadius:99, padding:"10px 18px", cursor:"pointer", fontSize:15, fontWeight:800, color: picked === w.es ? "#fff" : "#333" }}>
                {w.es}
              </button>
            ))}
          </div>
          <button disabled={!picked} onClick={() => { if (picked) { onSaidIt(picked); setDone(true); } }}
            style={{ padding:"14px 36px", borderRadius:99, background: picked ? "#FF6B9D" : "#eee", border:"none", color: picked ? "#fff" : "#aaa", fontSize:16, fontWeight:800, cursor: picked ? "pointer" : "default" }}>
            Confirm
          </button>
        </>
      ) : (
        <>
          <div style={{ fontSize:72, animation:"bop 0.6s ease-in-out infinite", marginBottom:12 }}>🐾</div>
          <div style={{ fontSize:22, fontWeight:800, color:"#333", marginBottom:8 }}>Mochachito would be so proud!</div>
          <div style={{ fontSize:16, color:"#888", marginBottom:"1.5rem" }}>"{picked}" in real life — that is real learning!</div>
          <button onClick={() => { setDone(false); setPicked(null); setView("home"); }}
            style={{ padding:"14px 32px", borderRadius:99, background:"#00BCD4", border:"none", color:"#fff", fontSize:16, fontWeight:800, cursor:"pointer" }}>
            Back
          </button>
        </>
      )}
    </div>
  );

  if (view === "wordbank") return (
    <div style={{ flex:1, overflowY:"auto", padding:"1rem" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:"0.75rem" }}>
        <button onClick={() => setView("home")} style={{ background:"#f0f0f0", border:"none", borderRadius:99, padding:"6px 14px", cursor:"pointer", fontSize:13, fontWeight:700 }}>Back</button>
        <div style={{ fontSize:16, fontWeight:800, color:"#333" }}>Word Bank</div>
      </div>
      {exposed.length === 0 && <div style={{ color:"#aaa", fontSize:14, textAlign:"center", marginTop:"2rem" }}>No words yet — start a lesson!</div>}
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {exposed.map(w => (
          <div key={w.es} style={{ background:"#f8f9fa", borderRadius:16, padding:"10px 14px", display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:44, height:32, borderRadius:8, overflow:"hidden", flexShrink:0 }}>
              <WordArt art={w.art} color={w.col} size={90}/>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                <span style={{ fontSize:15, fontWeight:800, color: w.col }}>{w.es}</span>
                <span style={{ fontSize:12, color:"#aaa" }}>= {w.en}</span>
              </div>
              <div style={{ fontSize:11, color:"#888", marginTop:2 }}>{confLabel(w)}</div>
            </div>
            {w.saidIt && <div style={{ fontSize:20 }}>💬</div>}
          </div>
        ))}
      </div>
    </div>
  );

  if (view === "snapshot") return (
    <div style={{ flex:1, overflowY:"auto", padding:"1rem" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:"1rem" }}>
        <button onClick={() => setView("home")} style={{ background:"#f0f0f0", border:"none", borderRadius:99, padding:"6px 14px", cursor:"pointer", fontSize:13, fontWeight:700 }}>Back</button>
        <div style={{ fontSize:16, fontWeight:800, color:"#333" }}>Weekly View</div>
      </div>
      <div style={{ background:"linear-gradient(135deg,#E0F7FA,#E8F5E9)", borderRadius:20, padding:"1.25rem", marginBottom:"1rem" }}>
        <div style={{ fontSize:15, fontWeight:800, color:"#006064", marginBottom:"0.75rem" }}>{name || "Your child"}'s journey</div>
        {[
          { label:"Words met",         val: exposed.length,    icon:"📚" },
          { label:"Heard a lot",        val: heardLot.length,  icon:"🔥" },
          { label:"Used in real life",  val: realLife.length,  icon:"💬" },
          { label:"Episodes completed", val: doneEps.length,   icon:"🎬" },
        ].map((s, i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, background:"rgba(255,255,255,0.7)", borderRadius:12, padding:"8px 12px" }}>
            <div style={{ fontSize:13, color:"#555" }}>{s.icon} {s.label}</div>
            <div style={{ fontSize:18, fontWeight:800, color:"#00ACC1" }}>{s.val}</div>
          </div>
        ))}
      </div>
      <div style={{ background:"#F8F9FA", borderRadius:16, padding:"0.9rem" }}>
        <div style={{ fontSize:12, fontWeight:800, color:"#555", marginBottom:6 }}>Parent tip</div>
        <div style={{ fontSize:12, color:"#888", lineHeight:1.5 }}>After 10 sessions ask: can she say 3 words she did not know before? That is the real measure — not scores or streaks.</div>
      </div>
    </div>
  );

  // home view
  return (
    <div style={{ flex:1, overflowY:"auto", padding:"1rem" }}>
      <div style={{ background:"linear-gradient(135deg,#E0F7FA,#E8F5E9)", borderRadius:20, padding:"1.25rem", marginBottom:"1rem" }}>
        <div style={{ fontSize:14, fontWeight:800, color:"#006064", marginBottom:6 }}>Quick check-in</div>
        <div style={{ fontSize:13, color:"#555", marginBottom:"0.75rem" }}>Did {name || "your child"} use any Spanish words outside the app today?</div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => setView("saidit")} style={{ flex:1, padding:"10px", borderRadius:14, background:"#00BCD4", border:"none", color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer" }}>Yes</button>
          <button style={{ flex:1, padding:"10px", borderRadius:14, background:"#fff", border:"2px solid #ddd", color:"#888", fontSize:13, fontWeight:700, cursor:"pointer" }}>Not sure</button>
          <button style={{ flex:1, padding:"10px", borderRadius:14, background:"#fff", border:"2px solid #ddd", color:"#888", fontSize:13, fontWeight:700, cursor:"pointer" }}>Skip</button>
        </div>
      </div>
      <button onClick={() => setView("saidit")}
        style={{ width:"100%", padding:"16px", borderRadius:20, background:"linear-gradient(135deg,#FF6B9D,#FF4081)", border:"none", color:"#fff", fontSize:17, fontWeight:800, cursor:"pointer", marginBottom:"1rem", boxShadow:"0 4px 16px rgba(255,107,157,0.4)", animation:"pulse 2.5s ease-in-out infinite" }}>
        She Said It! (log a real-life word)
      </button>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:"1rem" }}>
        {[
          { label:"Words met",    val: exposed.length,   col:"#00ACC1" },
          { label:"Real-life",    val: realLife.length,  col:"#FF6B9D" },
          { label:"Episodes",     val: doneEps.length,   col:"#388E3C" },
          { label:"Heard a lot",  val: heardLot.length,  col:"#FF9800" },
        ].map((s, i) => (
          <div key={i} style={{ background:"#fff", borderRadius:16, padding:"0.9rem", borderLeft:"4px solid " + s.col, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:22, fontWeight:800, color: s.col }}>{s.val}</div>
            <div style={{ fontSize:11, fontWeight:700, color:"#aaa", marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:"1rem" }}>
        <button onClick={() => setView("wordbank")} style={{ padding:"14px", borderRadius:16, background:"#fff", border:"2px solid #00BCD4", color:"#00ACC1", fontSize:14, fontWeight:800, cursor:"pointer" }}>Word Bank</button>
        <button onClick={() => setView("snapshot")} style={{ padding:"14px", borderRadius:16, background:"#fff", border:"2px solid #388E3C", color:"#388E3C", fontSize:14, fontWeight:800, cursor:"pointer" }}>Weekly View</button>
      </div>
      <div style={{ background:"#F8F9FA", borderRadius:16, padding:"0.9rem" }}>
        <div style={{ fontSize:12, fontWeight:800, color:"#555", marginBottom:6 }}>Parent tip</div>
        <div style={{ fontSize:12, color:"#888", lineHeight:1.5 }}>Consistency beats intensity. 5 minutes every day is better than 1 hour once a week.</div>
      </div>
    </div>
  );
}

function ParentModal({ name, doneEps, wordBank, onSaidIt, onClose }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:200, display:"flex", flexDirection:"column" }} onClick={onClose}>
      <div style={{ background:"#fff", borderRadius:"28px 28px 0 0", marginTop:"auto", display:"flex", flexDirection:"column", maxHeight:"90vh" }} onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"1.25rem 1.25rem 0" }}>
          <div style={{ fontSize:18, fontWeight:800, color:"#333" }}>Parent Dashboard</div>
          <button onClick={onClose} style={{ background:"#f0f0f0", border:"none", borderRadius:99, padding:"7px 16px", cursor:"pointer", fontSize:14, fontWeight:700 }}>Close</button>
        </div>
        <ParentDash name={name} doneEps={doneEps} wordBank={wordBank} onSaidIt={onSaidIt}/>
      </div>
    </div>
  );
}

/* ─── MAIN APP ────────────────────────────────────────────── */
export default function App() {
  const [screen,     setScreen]    = useState("home");
  const [name,       setName]      = useState("");
  const [inp,        setInp]       = useState("");
  const [worldId,    setWorldId]   = useState(null);
  const [epId,       setEpId]      = useState(null);
  const [step,       setStep]      = useState("watch");
  const [wordIdx,    setWordIdx]   = useState(0);
  const [qRound,     setQRound]    = useState(0);
  const [qOpts,      setQOpts]     = useState([]);
  const [qAns,       setQAns]      = useState(null);
  const [qScore,     setQScore]    = useState(0);
  const [songLine,   setSongLine]  = useState(0);
  const [singing,    setSinging]   = useState(false);
  const [mood,       setMood]      = useState("idle");
  const [cocoAnim,   setCocoAnim]  = useState("fl");
  const [bubble,     setBubble]    = useState("Hola! I am Mochachito!");
  const [confetti,   setConfetti]  = useState(false);
  const [stars,      setStars]     = useState(0);
  const [doneEps,    setDoneEps]   = useState([]);
  const [showVoice,  setShowVoice] = useState(false);
  const [showParent, setShowParent]= useState(false);
  const [wordBank,   setWordBank]  = useState(() => ALL_WORDS.map(w => ({ ...w, count:0, saidIt:false })));

  const { say, list: voiceList, selName, pick: pickVoice } = useTTS();
  const snd = useSnd();

  const W  = worldId ? WORLDS.find(w => w.id === worldId) : null;
  const EP = epId && W ? W.episodes.find(e => e.id === epId) : null;
  const col = W ? W.col : "#00ACC1";

  const sayBubble = useCallback((txt, m = "idle", a = "fl", dur = 3000) => {
    setBubble(txt); setMood(m); setCocoAnim(a);
    if (m !== "idle") {
      const t = setTimeout(() => { setMood("idle"); setCocoAnim("fl"); }, dur);
      return () => clearTimeout(t);
    }
  }, []);

  const bumpWord = useCallback((es) => {
    setWordBank(wb => wb.map(w => w.es === es ? { ...w, count: w.count + 1 } : w));
  }, []);

  const speakWord = useCallback((word, lang = "es") => {
    const txt = lang === "es" ? word.es : word.en;
    say(txt, lang, 0.74, 1.15);
    if (lang === "es") { bumpWord(word.es); track("word_heard", { word: word.es }); }
  }, [say, bumpWord]);

  const handleSaidIt = useCallback((es) => {
    setWordBank(wb => wb.map(w => w.es === es ? { ...w, saidIt:true } : w));
    track("word_said", { word: es });
    snd.stamp();
  }, [snd]);

  const openWorld = (wid) => {
    snd.tap();
    setWorldId(wid);
    setScreen("world");
    const w = WORLDS.find(x => x.id === wid);
    track("world_opened", { world: wid });
    sayBubble("Bienvenido to " + w.name + "!", "cheer", "bop", 2000);
  };

  const openEp = (eid) => {
    snd.tap();
    setEpId(eid);
    setStep("watch");
    setWordIdx(0);
    setQScore(0);
    setSongLine(0);
    setScreen("episode");
    track("episode_started", { world: worldId, episode: eid });
    sayBubble("Tap to hear each word!", "curious");
  };

  const buildQ = useCallback((ep, round) => {
    const correct = ep.vocab[round % ep.vocab.length];
    const others  = shuffle(ep.vocab.filter(v => v.es !== correct.es)).slice(0, 3);
    setQOpts(shuffle([correct, ...others]));
    setQAns(null);
    say(correct.es, "es", 0.72, 1.14);
    bumpWord(correct.es);
  }, [say, bumpWord]);

  const handleQ = (opt) => {
    if (qAns) return;
    setQAns(opt.es);
    const correct = EP.vocab[qRound % EP.vocab.length];
    say(opt.es, "es", 0.72, 1.14);
    bumpWord(opt.es);
    track("quiz_answer", { word: correct.es, correct: opt.es === correct.es, episode: epId, round: qRound });
    if (opt.es === correct.es) {
      snd.yay();
      sayBubble(correct.es.toUpperCase() + "! Amazing!", "cheer", "bop", 1600);
      setQScore(s => s + 1);
    } else {
      snd.oops();
      setTimeout(() => say(correct.es, "es", 0.72, 1.14), 700);
      sayBubble("It is " + correct.es + "! You got this!", "wrong", "shake", 1000);
    }
    setTimeout(() => {
      const next = qRound + 1;
      if (next >= EP.vocab.length * 2) { finishEp(); return; }
      setQRound(next);
      buildQ(EP, next);
    }, 1800);
  };

  const finishEp = () => {
    snd.magic();
    track("episode_completed", { world: worldId, episode: epId, score: qScore, total: EP ? EP.vocab.length * 2 : null });
    if (!doneEps.includes(epId)) setDoneEps(d => [...d, epId]);
    setStars(s => s + 3);
    setConfetti(true);
    setTimeout(() => setConfetti(false), 3500);
    sayBubble("Fantastico " + (name || "amigo") + "! You are amazing!", "excited", "bop", 6000);
    setStep("celebrate");
  };

  const singLine = () => {
    if (singing) return;
    setSinging(true);
    say(EP.song.lines[songLine], "es", 0.68, 1.16);
    setTimeout(() => {
      setSinging(false);
      if (songLine < EP.song.lines.length - 1) {
        setSongLine(l => l + 1);
      } else {
        track("song_completed", { world: worldId, episode: epId });
        setStep("play");
        setQRound(0);
        buildQ(EP, 0);
        sayBubble("Tap the right picture!", "cheer", "bop", 2000);
        snd.yay();
      }
    }, 2200);
  };

  /* shared style helpers */
  const pageBg  = { minHeight:"100vh", background: W ? W.bg : "linear-gradient(170deg,#0097A7,#26C6A6 60%,#FFF176)", padding:"1rem" };
  const BackBtn = ({ to }) => (
    <button onClick={() => { snd.tap(); to(); }}
      style={{ background:"rgba(255,255,255,0.92)", border:"none", borderRadius:99, padding:"9px 18px", fontSize:14, fontWeight:800, color:"#333", cursor:"pointer", marginBottom:"0.75rem", boxShadow:"0 2px 8px rgba(0,0,0,0.1)" }}>
      Back
    </button>
  );
  const CocoRow = ({ size = 90 }) => (
    <div style={{ display:"flex", alignItems:"flex-end", gap:8, marginBottom:"0.75rem", justifyContent:"center" }}>
      <div style={{ animation: cocoAnim + " " + (cocoAnim === "bop" ? "0.55s" : "3s") + " ease-in-out infinite", display:"inline-block" }}>
        <Coco mood={mood} size={size} col={col}/>
      </div>
      <div style={{ flex:1, background:"rgba(255,255,255,0.95)", border:"3px solid " + col, borderRadius:18, padding:"10px 14px", boxShadow:"0 4px 12px " + col + "44" }}>
        <p style={{ margin:0, fontSize:13, fontWeight:800, color:"#333", lineHeight:1.4 }}>{bubble}</p>
      </div>
    </div>
  );
  const Overlays = () => (
    <>
      {showVoice  && <VoicePicker voices={voiceList} selected={selName} onPick={pickVoice} onClose={() => setShowVoice(false)}/>}
      {showParent && <ParentModal name={name} doneEps={doneEps} wordBank={wordBank} onSaidIt={handleSaidIt} onClose={() => setShowParent(false)}/>}
    </>
  );

  /* ── HOME ─────────────────────────────────────────────── */
  if (screen === "home") return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(170deg,#0097A7,#26C6A6 60%,#FFF176)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"space-between", padding:"1.5rem", textAlign:"center" }}>
      <style>{CSS}</style>
      <div style={{ paddingTop:"0.5rem" }}>
        <div style={{ fontSize:46, fontWeight:900, letterSpacing:-2, background:"linear-gradient(90deg,#fff,#FFD700,#FF6B9D)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>Mochachito</div>
        <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.8)", letterSpacing:3, textTransform:"uppercase" }}>A Latin American Adventure</div>
      </div>
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", flex:1, justifyContent:"center", width:"100%" }}>
        <div style={{ background:"#fff", border:"5px solid #FFD700", borderRadius:28, padding:"14px 22px", maxWidth:290, boxShadow:"0 8px 28px rgba(0,0,0,0.15)", marginBottom:22, position:"relative" }}>
          <div style={{ position:"absolute", bottom:-18, left:"50%", transform:"translateX(-50%)", width:0, height:0, borderLeft:"14px solid transparent", borderRight:"14px solid transparent", borderTop:"18px solid #FFD700" }}/>
          <div style={{ position:"absolute", bottom:-12, left:"50%", transform:"translateX(-50%)", width:0, height:0, borderLeft:"11px solid transparent", borderRight:"11px solid transparent", borderTop:"13px solid #fff" }}/>
          <p style={{ margin:0, fontSize:17, fontWeight:800, color:"#333", lineHeight:1.4 }}>Hola! I am Mochachito! What is your name?</p>
        </div>
        <div style={{ animation:"pop 0.7s ease-out", display:"inline-block", marginBottom:34 }}>
          <Coco mood="idle" still size={210} col="#FFD700"/>
        </div>
        <div style={{ display:"flex", gap:8, width:"100%", maxWidth:320, boxSizing:"border-box" }}>
        <input value={inp} onChange={e => setInp(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && inp.trim()) { snd.magic(); say("Hola " + inp.trim() + " bienvenido", "es", 0.75, 1.15); setName(inp.trim()); track("adventure_started"); setScreen("map"); } }}
          placeholder="My name is..."
          size={1}
          style={{ flex:1, minWidth:0, boxSizing:"border-box", padding:"16px 20px", borderRadius:99, border:"4px solid #FFD700", fontSize:18, fontWeight:700, outline:"none", background:"rgba(255,255,255,0.97)", color:"#333" }}/>
        <button onClick={() => { if (inp.trim()) { snd.magic(); say("Hola " + inp.trim() + " bienvenido", "es", 0.75, 1.15); setName(inp.trim()); track("adventure_started"); setScreen("map"); } }}
          style={{ flexShrink:0, padding:"16px 22px", borderRadius:99, background:"#FFD700", border:"none", color:"#1a0c00", fontSize:22, fontWeight:900, cursor:"pointer", boxShadow:"0 4px 16px rgba(255,215,0,0.5)", animation:"pulse 2s ease-in-out infinite" }}>
          Go
        </button>
        </div>
      </div>
      <div style={{ display:"flex", gap:10, paddingBottom:"0.75rem" }}>
        <button onClick={() => setShowVoice(true)}  style={{ background:"rgba(255,255,255,0.22)", border:"2px solid rgba(255,255,255,0.45)", borderRadius:99, padding:"9px 18px", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>Voice ({voiceList.length})</button>
        <button onClick={() => setShowParent(true)} style={{ background:"rgba(255,255,255,0.22)", border:"2px solid rgba(255,255,255,0.45)", borderRadius:99, padding:"9px 18px", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>Parents</button>
      </div>
      <Overlays/>
    </div>
  );

  /* ── MAP ──────────────────────────────────────────────── */
  if (screen === "map") return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(170deg,#0097A7,#26C6A6 50%,#FFF176)", padding:"1rem" }}>
      <style>{CSS}</style>
      {confetti && <Confetti/>}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"0.75rem" }}>
        <div style={{ fontSize:22, fontWeight:900, background:"linear-gradient(90deg,#fff,#FFD700)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>Mochachito</div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => setShowVoice(true)}  style={{ background:"rgba(255,255,255,0.9)", border:"none", borderRadius:99, padding:"7px 14px", fontSize:13, fontWeight:800, cursor:"pointer", color:"#333" }}>Voice</button>
          <button onClick={() => setShowParent(true)} style={{ background:"rgba(255,255,255,0.9)", border:"none", borderRadius:99, padding:"7px 14px", fontSize:13, fontWeight:800, cursor:"pointer", color:"#333" }}>Parents</button>
          <div style={{ background:"rgba(255,255,255,0.9)", borderRadius:99, padding:"6px 14px", display:"flex", alignItems:"center", gap:5 }}>
            <span style={{ fontSize:16 }}>⭐</span>
            <span style={{ fontWeight:800, fontSize:15, color:"#333" }}>{stars}</span>
          </div>
        </div>
      </div>
      <div style={{ background:"rgba(255,255,255,0.94)", borderRadius:28, padding:"1rem", marginBottom:"1rem", display:"flex", alignItems:"center", gap:12, boxShadow:"0 6px 20px rgba(0,0,0,0.09)" }}>
        <div style={{ animation: cocoAnim + " 3s ease-in-out infinite", display:"inline-block", flexShrink:0 }}>
          <Coco mood={mood} size={96} col="#00ACC1"/>
        </div>
        <div style={{ flex:1, background:"#E0F7FA", border:"3px solid #00BCD4", borderRadius:18, padding:"10px 14px" }}>
          <p style={{ margin:0, fontSize:14, fontWeight:800, color:"#006064", lineHeight:1.4 }}>{bubble}</p>
        </div>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        {WORLDS.map((w, wi) => {
          const epsDone = w.episodes.filter(e => doneEps.includes(e.id)).length;
          return (
            <button key={w.id} onClick={() => openWorld(w.id)}
              style={{ background:"rgba(255,255,255,0.95)", border:"4px solid " + w.col, borderRadius:28, padding:0, cursor:"pointer", overflow:"hidden", boxShadow:"0 8px 24px " + w.col + "55", transition:"transform 0.15s", textAlign:"left" }}
              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.02)"}
              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
              <div style={{ position:"relative", height:120, overflow:"hidden" }}>
                <Scene worldId={w.id}/>
                <div style={{ position:"absolute", inset:0, background:"linear-gradient(to bottom,transparent 55%,rgba(255,255,255,0.97) 100%)" }}/>
                <div style={{ position:"absolute", top:8, left:12, background:"rgba(0,0,0,0.38)", borderRadius:99, padding:"4px 14px" }}>
                  <span style={{ fontSize:11, fontWeight:800, color:"#fff", letterSpacing:2, textTransform:"uppercase" }}>World {wi+1}</span>
                </div>
                {epsDone > 0 && (
                  <div style={{ position:"absolute", top:8, right:12, background: w.col, borderRadius:99, padding:"4px 14px" }}>
                    <span style={{ fontSize:11, fontWeight:800, color:"#fff" }}>{epsDone}/{w.episodes.length} done</span>
                  </div>
                )}
              </div>
              <div style={{ padding:"0.6rem 1rem 1rem" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
                  <span style={{ fontSize:26 }}>{w.flag}</span>
                  <div style={{ fontSize:18, fontWeight:900, color:"#333" }}>{w.name}</div>
                </div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {w.episodes.map(ep => (
                    <div key={ep.id} style={{ background: doneEps.includes(ep.id) ? w.col : w.col + "22", borderRadius:12, padding:"4px 12px", fontSize:12, fontWeight:800, color: doneEps.includes(ep.id) ? "#fff" : w.col }}>
                      {ep.icon} {ep.title}{doneEps.includes(ep.id) ? " done" : ""}
                    </div>
                  ))}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <p style={{ textAlign:"center", fontSize:11, color:"rgba(255,255,255,0.5)", marginTop:"1rem", fontWeight:700 }}>5 min episodes · No ads · Always free</p>
      <Overlays/>
    </div>
  );

  /* ── WORLD ────────────────────────────────────────────── */
  if (screen === "world" && W) return (
    <div style={pageBg}><style>{CSS}</style>
      <BackBtn to={() => setScreen("map")}/>
      <div style={{ borderRadius:24, overflow:"hidden", boxShadow:"0 8px 24px rgba(0,0,0,0.15)", marginBottom:"0.75rem" }}>
        <Scene worldId={worldId}/>
      </div>
      <CocoRow size={108}/>
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {W.episodes.map((ep, ei) => {
          const done = doneEps.includes(ep.id);
          return (
            <button key={ep.id} onClick={() => openEp(ep.id)}
              style={{ background:"rgba(255,255,255,0.95)", border:"4px solid " + (done ? "#388E3C" : col), borderRadius:24, padding:"1.1rem 1.25rem", cursor:"pointer", display:"flex", alignItems:"center", gap:14, boxShadow:"0 6px 20px " + col + "44", transition:"transform 0.15s", textAlign:"left" }}
              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.02)"}
              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
              <div style={{ width:62, height:62, borderRadius:18, background: done ? "#E8F5E9" : col + "22", border:"3px solid " + (done ? "#388E3C" : col), display:"flex", alignItems:"center", justifyContent:"center", fontSize:30, flexShrink:0 }}>
                {done ? "✅" : ep.icon}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:19, fontWeight:900, color:"#333" }}>{ep.title}</div>
                <div style={{ fontSize:12, color:"#999", marginTop:3 }}>{ep.vocab.length} words · Song · Game</div>
              </div>
              <div style={{ background: done ? "#388E3C" : col, color:"#fff", borderRadius:99, padding:"12px 20px", fontSize:15, fontWeight:800, flexShrink:0, animation:"pulse 2s ease-in-out infinite" }}>
                {done ? "Replay" : "Play"}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ── EPISODE ──────────────────────────────────────────── */
  if (screen === "episode" && EP && W) {
    const word = EP.vocab[wordIdx];

    /* WATCH */
    if (step === "watch") return (
      <div style={pageBg}><style>{CSS}</style>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"0.75rem" }}>
          <BackBtn to={() => setScreen("world")}/>
          <div style={{ display:"flex", gap:6 }}>
            {EP.vocab.map((_, i) => (
              <div key={i} style={{ width:10, height:10, borderRadius:"50%", background: i <= wordIdx ? "#fff" : "rgba(255,255,255,0.35)", transition:"background 0.3s" }}/>
            ))}
          </div>
        </div>
        <div style={{ borderRadius:20, overflow:"hidden", boxShadow:"0 6px 20px rgba(0,0,0,0.12)", marginBottom:"0.75rem" }}>
          <Scene worldId={worldId}/>
        </div>
        <CocoRow size={90}/>
        <button
          onClick={() => { snd.pop(); speakWord(word, "es"); sayBubble(word.es + "!", "happy", "bop", 1400); }}
          style={{ width:"100%", background:"#fff", border:"6px solid " + word.col, borderRadius:28, overflow:"hidden", cursor:"pointer", boxShadow:"0 10px 32px " + word.col + "55", marginBottom:"0.75rem", animation:"slideUp 0.4s ease-out", textAlign:"center" }}>
          <WordArt art={word.art} color={word.col} size={320}/>
          <div style={{ padding:"0.6rem 1rem 1rem" }}>
            <div style={{ fontSize:44, fontWeight:900, color: word.col, letterSpacing:-1 }}>{word.es}</div>
            <div style={{ display:"inline-flex", alignItems:"center", gap:8, marginTop:10, background: word.col, color:"#fff", borderRadius:99, padding:"10px 28px", fontSize:16, fontWeight:800 }}>
              Tap to hear!
            </div>
          </div>
        </button>
        <div style={{ display:"flex", gap:10, marginBottom:"0.75rem" }}>
          <button onClick={() => speakWord(word, "es")} style={{ flex:1, padding:"16px", borderRadius:20, background:"rgba(255,255,255,0.92)", border:"4px solid " + word.col, fontSize:22, fontWeight:900, color: word.col, cursor:"pointer" }}>🇲🇽</button>
          <button onClick={() => speakWord(word, "en")} style={{ flex:1, padding:"16px", borderRadius:20, background:"rgba(255,255,255,0.92)", border:"4px solid #5C6BC0",   fontSize:22, fontWeight:900, color:"#5C6BC0",   cursor:"pointer" }}>🇺🇸</button>
        </div>
        <button
          onClick={() => {
            snd.tap();
            if (wordIdx < EP.vocab.length - 1) {
              const nw = EP.vocab[wordIdx + 1];
              setWordIdx(i => i + 1);
              speakWord(nw, "es");
              sayBubble(nw.es + "!", "curious");
            } else {
              setStep("sing");
              setSongLine(0);
              sayBubble("Now let us sing!", "sing");
            }
          }}
          style={{ width:"100%", padding:"18px", borderRadius:99, background: col, border:"none", color:"#fff", fontSize:20, fontWeight:900, cursor:"pointer", boxShadow:"0 6px 20px " + col + "66", animation:"pulse 2s ease-in-out infinite" }}>
          {wordIdx < EP.vocab.length - 1 ? "Next" : "Sing!"}
        </button>
      </div>
    );

    /* SING */
    if (step === "sing") return (
      <div style={pageBg}><style>{CSS}</style>
        <BackBtn to={() => setStep("watch")}/>
        <div style={{ textAlign:"center", marginBottom:"0.75rem" }}>
          <div style={{ animation:"bop 0.7s ease-in-out infinite", display:"inline-block" }}>
            <Coco mood="sing" size={140} col={col}/>
          </div>
        </div>
        <div style={{ background:"rgba(255,255,255,0.95)", borderRadius:24, padding:"1.25rem", boxShadow:"0 6px 20px rgba(0,0,0,0.1)" }}>
          <div style={{ fontSize:18, fontWeight:900, color: col, marginBottom:12, textAlign:"center" }}>{EP.song.title}</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:"1rem" }}>
            {EP.song.lines.map((line, i) => (
              <div key={i} style={{
                background: i === songLine ? "#fff" : i < songLine ? col + "22" : "#f5f5f5",
                border:"3px solid " + (i === songLine ? col : "transparent"),
                borderRadius:16, padding:"14px 18px", fontSize:18, fontWeight:800,
                color: i === songLine ? col : i < songLine ? "#bbb" : "#ddd",
                transition:"all 0.3s",
                boxShadow: i === songLine ? "0 4px 14px " + col + "44" : "none",
                animation: i === songLine && singing ? "pulse 0.4s ease-in-out infinite" : undefined,
              }}>
                {i < songLine ? "OK  " : i === songLine ? ">> " : ""}{line}
              </div>
            ))}
          </div>
          <button onClick={singLine} disabled={singing}
            style={{ width:"100%", padding:"16px", borderRadius:99, background: singing ? col + "88" : col, border:"none", color:"#fff", fontSize:18, fontWeight:800, cursor: singing ? "default" : "pointer", boxShadow:"0 4px 16px " + col + "55", animation:"pulse 2s ease-in-out infinite" }}>
            {singing ? "Singing..." : songLine === 0 ? "Start Singing!" : "Next line"}
          </button>
        </div>
      </div>
    );

    /* PLAY */
    if (step === "play") {
      const target = EP.vocab[qRound % EP.vocab.length];
      return (
        <div style={pageBg}><style>{CSS}</style>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"0.75rem" }}>
            <BackBtn to={() => setStep("sing")}/>
            <div style={{ background:"rgba(255,255,255,0.9)", borderRadius:99, padding:"6px 16px", fontWeight:800, fontSize:14, color: col }}>
              Score: {qScore}
            </div>
          </div>
          <CocoRow size={90}/>
          <div style={{ background:"rgba(255,255,255,0.95)", border:"5px solid " + col, borderRadius:26, padding:"1.25rem", textAlign:"center", marginBottom:"0.75rem", boxShadow:"0 8px 24px " + col + "44" }}>
            <button onClick={() => { say(target.es, "es", 0.72, 1.14); bumpWord(target.es); }}
              style={{ background: col, border:"none", borderRadius:99, padding:"14px 36px", fontSize:22, fontWeight:900, color:"#fff", cursor:"pointer", animation:"pulse 1.8s ease-in-out infinite" }}>
              {target.es}
            </button>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {qOpts.map(opt => {
              let border = "3px solid #eee", bg = "#fff";
              if (qAns) {
                if (opt.es === target.es)             { border = "4px solid #388E3C"; bg = "#E8F5E9"; }
                else if (opt.es === qAns)              { border = "4px solid #C62828"; bg = "#FFEBEE"; }
              }
              return (
                <button key={opt.es} onClick={() => handleQ(opt)} disabled={!!qAns}
                  style={{ background: bg, border, borderRadius:22, padding:"0.4rem", cursor: qAns ? "default" : "pointer", boxShadow:"0 4px 14px rgba(0,0,0,0.07)", transition:"all 0.2s", overflow:"hidden" }}>
                  <div style={{ borderRadius:14, overflow:"hidden" }}>
                    <WordArt art={opt.art} color={opt.col} size={160}/>
                  </div>
                  {qAns && opt.es === target.es && <div style={{ fontSize:24, padding:"4px 0" }}>✅</div>}
                  {qAns && opt.es === qAns && opt.es !== target.es && <div style={{ fontSize:24, padding:"4px 0" }}>❌</div>}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    /* CELEBRATE */
    if (step === "celebrate") return (
      <div style={{ minHeight:"100vh", background: W.bg, padding:"1rem" }}>
        <style>{CSS}</style>
        {confetti && <Confetti/>}
        <div style={{ borderRadius:20, overflow:"hidden", boxShadow:"0 6px 20px rgba(0,0,0,0.15)", marginBottom:"0.75rem" }}>
          <Scene worldId={worldId}/>
        </div>
        <div style={{ textAlign:"center", marginBottom:"0.5rem" }}>
          <div style={{ animation:"bop 0.6s ease-in-out infinite", display:"inline-block" }}>
            <Coco mood="excited" size={145} col={col}/>
          </div>
        </div>
        <div style={{ background:"rgba(255,255,255,0.97)", border:"4px solid " + col, borderRadius:28, padding:"1.25rem", marginBottom:"1rem", boxShadow:"0 8px 24px " + col + "55", animation:"slideUp 0.5s ease-out" }}>
          <div style={{ fontSize:24, fontWeight:900, color: col, marginBottom:4, textAlign:"center" }}>Fantastico!</div>
          <div style={{ display:"flex", justifyContent:"center", gap:6, marginBottom:"0.75rem" }}>
            {[1,2,3].map(s => <span key={s} style={{ fontSize:36, animation:"starPop 0.5s ease-out " + (s*0.15) + "s both" }}>⭐</span>)}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {EP.vocab.map(w => (
              <div key={w.es} style={{ display:"flex", alignItems:"center", gap:10, background:"#f8f9fa", borderRadius:14, padding:"8px 12px" }}>
                <div style={{ width:50, height:36, borderRadius:10, overflow:"hidden", flexShrink:0 }}>
                  <WordArt art={w.art} color={w.col} size={110}/>
                </div>
                <div style={{ display:"flex", gap:8, flex:1, flexWrap:"wrap" }}>
                  <button onClick={() => speakWord(w, "es")} style={{ background: w.col + "22", border:"2px solid " + w.col, borderRadius:99, padding:"6px 16px", fontSize:15, fontWeight:800, color: w.col, cursor:"pointer" }}>
                    🇲🇽 {w.es}
                  </button>
                  <button onClick={() => speakWord(w, "en")} style={{ background:"#e8eaf6", border:"2px solid #5C6BC0", borderRadius:99, padding:"6px 16px", fontSize:15, fontWeight:800, color:"#5C6BC0", cursor:"pointer" }}>
                    🇺🇸 {w.en}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background:"rgba(255,255,255,0.95)", border:"3px solid #FFB300", borderRadius:22, padding:"1rem", marginBottom:"1rem", boxShadow:"0 4px 14px rgba(255,179,0,0.2)" }}>
          <div style={{ fontSize:14, fontWeight:800, color:"#FF9800", marginBottom:6 }}>Take it outside!</div>
          <div style={{ background:"#FFF8E1", borderRadius:12, padding:"8px 12px", marginBottom:6, fontSize:13, fontWeight:700, color:"#555" }}>{EP.activity}</div>
          <div style={{ background:"#E8F5E9", borderRadius:12, padding:"8px 12px", fontSize:13, fontWeight:700, color:"#555" }}>{EP.realWorld}</div>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={() => { setStep("watch"); setWordIdx(0); setQScore(0); setSongLine(0); }}
            style={{ flex:1, padding:"14px", borderRadius:99, background:"rgba(255,255,255,0.9)", border:"3px solid " + col, color: col, fontSize:14, fontWeight:800, cursor:"pointer" }}>
            Replay
          </button>
          <button onClick={() => setScreen("world")}
            style={{ flex:1, padding:"14px", borderRadius:99, background: col, border:"none", color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", boxShadow:"0 4px 12px " + col + "55" }}>
            More!
          </button>
        </div>
      </div>
    );
  }

  return null;
}